import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import type { Redemption } from '@Types/redemption';
import { useRedemptions } from '@Hooks/useRedemptions';
import { getMemberProfile } from '@Utils/fish';
import { Receipt } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const formatStatus = (status: string) => {
  if (status === 'APPLIED') return 'Applied';
  if (status === 'REVERTED') return 'Reverted';
  return 'Pending';
};

const formatDiscountType = (discountType: string) => {
  if (discountType === 'PERCENTAGE') return 'Percentage';
  if (discountType === 'FLATAMOUNT') return 'Flat Amount';
  return 'Price Override';
};

const formatDiscountValue = (row: Redemption) => {
  if (row.discountType === 'PERCENTAGE') return `${row.discountValue}%`;
  return `$${row.discountValue}`;
};

const redemptionColumnConfigs: ColumnConfig<Redemption>[] = [
  {
    id: 'fName',
    headerLabel: 'First Name',
    accessor: (row) =>
      getMemberProfile(row.fish)?.fName ??
      row.fish.fishProfiles[0].fName ??
      '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'lName',
    headerLabel: 'Last Name',
    accessor: (row) =>
      getMemberProfile(row.fish)?.lName ??
      row.fish.fishProfiles[0].lName ??
      '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'purchaseId',
    headerLabel: 'Purchase ID',
    accessor: (row) => row.purchaseId,
    type: 'text',
    size: 200,
    cell: (info) => {
      const purchaseId = info.row.original.purchaseId;
      if (!purchaseId) return <span className="text-zinc-400">-</span>;
      return (
        <Link
          to={`/purchases/${purchaseId}`}
          className="text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {purchaseId}
        </Link>
      );
    },
  },
  {
    id: 'discountType',
    headerLabel: 'Discount Type',
    accessor: (row) => formatDiscountType(row.discountType),
    type: 'text',
    size: 120,
  },
  {
    id: 'discountValue',
    headerLabel: 'Discount Value',
    accessor: (row) => formatDiscountValue(row),
    type: 'text',
    size: 120,
  },
  {
    id: 'discountAmount',
    headerLabel: 'Amount',
    accessor: (row) => `$${row.discountAmount}`,
    type: 'text',
    size: 100,
  },
  {
    id: 'status',
    headerLabel: 'Status',
    accessor: (row) => formatStatus(row.status),
    type: 'text',
    size: 100,
    cell: (info) => {
      const status = info.row.original.status;
      const className =
        status === 'APPLIED'
          ? 'font-medium text-green-600'
          : status === 'REVERTED'
            ? 'text-red-500'
            : 'text-zinc-400';
      return <span className={className}>{formatStatus(status)}</span>;
    },
  },
  {
    id: 'redeemedAt',
    headerLabel: 'Redeemed At',
    accessor: (row) => new Date(row.redeemedAt).toLocaleString(),
    type: 'text',
    size: 180,
  },
];

interface PromoCodeRedemptionListProps {
  promoCodeId: string;
}

export default function PromoCodeRedemptionList({
  promoCodeId,
}: PromoCodeRedemptionListProps) {
  const controller = useResourceTableController<Redemption>({
    columnsConfig: redemptionColumnConfigs,
    defaultSort: {
      id: 'redeemedAt',
      desc: true,
    },
    baseSearchParams: {
      'filter[promoCodeId]': promoCodeId,
    },
    buildFilterParams: () => ({}),
    initialPageSize: 10,
  });

  const {
    data: redemptions,
    status,
    isError,
    error,
  } = useRedemptions({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load Redemptions',
      );
    }
  }, [isError, error]);

  const rows: Redemption[] = redemptions?.data ?? [];
  const meta = redemptions?.meta;

  return (
    <ResourceTableView<Redemption>
      label="Redemptions"
      controller={controller}
      data={rows}
      meta={meta}
      status={status}
      summaryIcon={<Receipt className="h-4 w-4 text-zinc-500" />}
      summaryLabel="Total Redemptions: "
    />
  );
}
